import { useState, useEffect } from 'react';
import { Box, Flex, Text, Card, Avatar, Badge, Button, IconButton, Separator, Tabs, Spinner, Progress } from '@radix-ui/themes';
import { Pencil1Icon, CalendarIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import CircleProgress from '../../components/shared/CircleProgress';
import UserAvatar from '../../components/common/UserAvatar';
import { STATUS_COLORS } from '../../utils/constants';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Info, DateTime } from 'luxon';
import { reportService } from '../../services/reportService';
import LoadingIndicator from '../../components/shared/LoadingIndicator';

const SchoolTeacherView = ({ teacherData }) => {
    const { user, courses, performance_metrics } = teacherData;
    const [coursesSummary, setCoursesSummary] = useState(null);
    const [adherenceMetrics, setAdherenceMetrics] = useState(null);
    const [questionsMetrics, setQuestionsMetrics] = useState(null);
    const [studentsSummary, setStudentsSummary] = useState(null);
    const [fetching, setFetching] = useState({ courses: false, students: false });

    console.log('teacherData>>>>>', teacherData);

    useEffect(() => {
        if (teacherData.id) {
            fetchPerformanceMetrics();
        }
    }, [teacherData.id]);

    const fetchPerformanceMetrics = async () => {
        setFetching({ ...fetching, performance: true });
        try {
            const metrics = await reportService.getTeacherPerformanceMetrics(teacherData.id);
            console.log('performance metrics>>>>>', metrics);
            setAdherenceMetrics(metrics.filter(metric => metric.type === 'schedule_adherence'));
            setQuestionsMetrics(metrics.filter(metric => metric.type === 'questions_confirmed'));
        } catch (error) {
            console.error('Error fetching performance metrics:', error);
        } finally {
            setFetching({ ...fetching, performance: false });
        }
    };

    const fetchCoursesSummary = async () => {
        setFetching({ ...fetching, courses: true });
        try {
            const summary = await reportService.getTeacherCoursesReportSummary(teacherData.id);
            console.log('summary>>>>>', summary);
            // might remove courses summary
            setCoursesSummary(summary);
            const metrics = [];
            summary.forEach(course => {
                if (course.teaching_plans && course.teaching_plans.length > 0) {
                    course.teaching_plans.forEach(teaching_plan => {
                        teaching_plan.teaching_plan_performances.forEach(performance => {
                            metrics.push(performance);
                        });
                    });
                }
            });
            console.log('metrics>>>>>', metrics);
            setAdherenceMetrics(metrics);
        } catch (error) {
            console.error('Error fetching courses summary:', error);
        } finally {
            setFetching({ ...fetching, courses: false });
        }
    };

    const fetchStudentsSummary = async () => {
        setFetching({ ...fetching, students: true });
        try {
            const summary = await reportService.getTeacherStudentsReportSummary(teacherData.id);
            setStudentsSummary(summary);
        } catch (error) {
            console.error('Error fetching students summary:', error);
        } finally {
            setFetching({ ...fetching, students: false });
        }
    };



    // Helper function to get specific metric
    const getMetric = (type) => {
        return performance_metrics?.find(metric => metric.type === type);
    };

    // Get schedule adherence metric
    const scheduleMetricAverage = performance_metrics?.filter(metric => metric.type === 'schedule_adherence').reduce((acc, curr) => acc + curr.value, 0) / performance_metrics?.filter(metric => metric.type === 'schedule_adherence').length;
    const questionsMetricAverage = performance_metrics?.filter(metric => metric.type === 'questions_confirmed').reduce((acc, curr) => acc + curr.value, 0) / performance_metrics?.filter(metric => metric.type === 'questions_confirmed').length;

    const getDayName = (day) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[day];
    };

    // Add this helper function after getDayName
    const createChartData = (metrics) => {
        if (!metrics) return [];
        const adherence_metrics = metrics.filter(metric => metric.type === 'schedule_adherence');
        if (adherence_metrics.length == 0) return [];
        // Sort metrics by recorded_at
        const sortedMetrics = [...adherence_metrics].sort((a, b) =>
            new Date(a.recorded_at) - new Date(b.recorded_at)
        );

        return sortedMetrics.map(metric => ({
            date: new Date(metric.recorded_at).toLocaleDateString(),
            completion: metric.value
        }));
    };

    return (
        <Box width="100%" pt="4">
            {/* Teacher Profile Section */}
            {/* <Card mb="4"> */}
            <Flex gap="4" align="center" justify="between" mb="4">
                <Flex gap="4" align="center">
                    <UserAvatar
                        size="8"
                        user={user}
                        radius="full"
                    />
                    <Box style={{ flex: 1, marginRight: 'auto' }} align="left">
                        <Text size="4" as="p" mb="2">
                            {user?.firstname} {user?.lastname}
                        </Text>
                            <Badge color={STATUS_COLORS[teacherData.verification_status]}>{teacherData.verification_status}</Badge>
                        
                    </Box>
                </Flex>
                <Flex gap="1" direction="column" align="right">
                    <Button variant="ghost" size="1"><Pencil1Icon />Edit</Button>
                </Flex>
            </Flex>
            <Separator size="4" />
            {/* </Card> */}

            {/* Performance Metrics Section */}
            {!performance_metrics || performance_metrics.length == 0 ? (
                <Flex gap="4" mb="4">
                    <InfoCircledIcon color="indigo" />
                    <Text size="2" mt="2">No performance Data on this teacher</Text>
                </Flex>
            ) : (
                <Box py="4">
                    <Text size="2" weight="bold" mb="3">Performance Metrics</Text>
                    <Flex gap="4">
                        <Box style={{ textAlign: 'center' }}>
                            <CircleProgress
                                progress={scheduleMetricAverage}
                                size={80}
                                label={`${Math.round(scheduleMetricAverage)}%`}

                            />
                            <Text size="2" mt="2">Schedule Adherence</Text>
                        </Box>
                        {/* <Box style={{ textAlign: 'center' }}>
                        <CircleProgress
                            progress={questionsMetricAverage}
                            size={80}
                            label={questionsMetricAverage}
                            color="#00b341"
                        />
                        <Text size="2" mt="2">Questions Confirmed</Text>
                    </Box> */}
                    </Flex>

                    {
                        performance_metrics && performance_metrics.length > 0 && (
                            <Box mt="4" style={{ height: '200px', width: '100%' }}>
                                <Text size="2" weight="bold" mb="2">Completion Trend</Text>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={createChartData(performance_metrics)}
                                        margin={{
                                            top: 10,
                                            right: 10,
                                            left: 10,
                                            bottom: 10,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            style={{ fontSize: '12px' }}
                                            height={20}
                                        />
                                        <YAxis
                                            domain={[0, 100]}
                                            style={{ fontSize: '12px' }}
                                            width={30}
                                            tickCount={6}
                                        />
                                        <Tooltip
                                            contentStyle={{ fontSize: '12px' }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '12px' }}
                                            height={20}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="completion"
                                            stroke="#8884d8"
                                            name="Completion %"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        )
                    }
                </Box >)}

            {/* Last 7 Days Performance */}
            <Box mb="4">
                {/* <Text size="2" weight="bold" mb="3">Last 7 Days Performance</Text> */}
                <Card>
                    <Flex gap="1" justify="center">
                        {(() => {
                            const today = DateTime.now().startOf('day');
                            const last7Days = Array.from({ length: 7 }, (_, i) => {
                                return today.minus({ days: 6 - i });
                            });

                            return last7Days.map(date => {
                                const dayName = date.toFormat('ccc');
                                const dateStr = date.toFormat('yyyy-MM-dd');

                                const dayMetrics = performance_metrics?.filter(m =>
                                    m.recorded_at.startsWith(dateStr)
                                );

                                const scheduleAdherence = dayMetrics?.find(m =>
                                    m.type === 'schedule_adherence'
                                )?.value;

                                const questionsConfirmed = dayMetrics?.find(m =>
                                    m.type === 'questions_confirmed'
                                )?.value;

                                return (
                                    <Flex key={dateStr} style={{ width: '100px' }} direction="column" align="center" gap="1">
                                        <Text size="1" weight="bold">{dayName}</Text>
                                        <Text size="1" color="gray">
                                            {date.day}
                                        </Text>
                                        {scheduleAdherence !== undefined ? (
                                            <CircleProgress
                                                progress={scheduleAdherence}
                                                size={40}
                                                label={`${Math.round(scheduleAdherence)}%`}
                                                labelSize="1"
                                            />
                                        ) : (
                                            <Text size="1" color="gray">-</Text>
                                        )}
                                        {questionsConfirmed !== undefined && (
                                            <Text size="1">
                                                Q: {questionsConfirmed}
                                            </Text>
                                        )}
                                    </Flex>
                                );
                            });
                        })()}
                    </Flex>
                </Card>
            </Box>

            <Tabs.Root defaultValue="courses">
                <Tabs.List>
                    <Tabs.Trigger value="courses">Courses</Tabs.Trigger>
                    <Tabs.Trigger value="students">Students</Tabs.Trigger>
                </Tabs.List>

                <Box pt="3">
                    <Tabs.Content value="courses">
                        {fetching.courses && <LoadingIndicator />}
                        {coursesSummary?.length > 0 && !fetching.courses && (
                            coursesSummary.map((course) => (
                                <Flex key={course.id} direction="column" gap="1" width="100%">
                                    <Text size="2" weight="bold" mb="4">{course.level.name} | {course.subject.name}</Text>
                                    {course.teaching_plans.length == 0 && (
                                        <Flex gap="2" align="center">
                                            <InfoCircledIcon color="indigo" />
                                            <Text size="2" as="p" pb="2" color="indigo">No active teaching plans created for this course</Text>
                                        </Flex>
                                    )}
                                    {course.teaching_plans.map((plan) => (
                                        <Flex direction="row" justify="between" key={plan.id} mb="3">
                                            <Flex direction="column" gap="1" style={{ flex: 2 }}>
                                                <Text size="2" as="p" pb="2">Teaching Plan: {plan.period.title}</Text>
                                                <Flex color="gray" as="p" align="center" gap="1">
                                                    <CalendarIcon /><Text size="1" as="p">Duration From: {new Date(plan.period.start_date).toLocaleDateString()}  To: {new Date(plan.period.end_date).toLocaleDateString()}</Text>
                                                </Flex>
                                                <Flex gap="2" align="center">
                                                    <Text size="1" as="p">Status: <Badge color={STATUS_COLORS[plan.status]}>{plan.status}</Badge></Text>
                                                </Flex>
                                            </Flex>
                                            <Flex direction="column" gap="1">
                                                {plan.teaching_plan_performances?.length > 0 ? (
                                                    <Flex direction="column" gap="2">
                                                    <Flex gap="4">
                                                        {(() => {
                                                            const performances = plan.teaching_plan_performances;
                                                            const avgParticipants = Math.round(
                                                                performances.reduce((acc, curr) => acc + curr.participant_count, 0) / performances.length
                                                            );
                                                            const avgScore = Math.round(
                                                                performances.reduce((acc, curr) => acc + curr.average_score, 0) / performances.length
                                                            );
                                                            const avgMinScore = Math.round(
                                                                performances.reduce((acc, curr) => acc + curr.min_score, 0) / performances.length
                                                            );
                                                            const avgMaxScore = Math.round(
                                                                performances.reduce((acc, curr) => acc + curr.max_score, 0) / performances.length
                                                            );

                                                            return (
                                                                <Flex direction="column" justify="between" align="end" gap="1" maxWidth="150px">
                                                                    <Box width="100%">
                                                                        <Flex direction="row" justify="between" gap="2" align="center">
                                                                            <Text size="1" color="gray">Avg. Participants</Text>
                                                                            <Text size="2" weight="bold">{avgParticipants}</Text>
                                                                        </Flex>
                                                                    </Box>
                                                                    <Box width="100%">
                                                                        <Flex direction="row" justify="between" gap="2" align="center">
                                                                            <Text size="1" color="gray">Avg. Score</Text>
                                                                            <Text size="2" weight="bold">{avgScore}%</Text>
                                                                        </Flex>
                                                                        <Progress value={avgScore} size="1" color="green" />
                                                                    </Box>
                                                                    <Box width="100%">
                                                                        <Flex direction="row" justify="between" gap="2" align="center">
                                                                            <Text size="1" color="gray">Avg. Min Score</Text>
                                                                            <Text size="2" weight="bold">{avgMinScore}%</Text>
                                                                        </Flex>
                                                                        <Progress value={avgMinScore} size="1" color="green" />
                                                                    </Box>
                                                                    <Box width="100%">
                                                                        <Flex direction="row" justify="between" gap="2" align="center">
                                                                            <Text size="1" color="gray">Avg. Max Score</Text>
                                                                            <Text size="2" weight="bold">{avgMaxScore}%</Text>
                                                                        </Flex>
                                                                        <Progress value={avgMaxScore} size="1" color="green" />
                                                                    </Box>
                                                                </Flex>
                                                            );
                                                        })()}
                                                    </Flex>
                                                </Flex>
                                                ) : (
                                                    <Card>
                                                        <Text size="2" as="p" pb="2">No performance data</Text>
                                                    </Card>
                                                    
                                                )}
                                            </Flex>
                                        </Flex>
                                    ))}
                                </Flex>
                            ))
                        )}
                    </Tabs.Content>

                    <Tabs.Content value="students">
                        {fetching.students ? <LoadingIndicator /> : <Text size="2">Teacher's students.</Text>}
                    </Tabs.Content>
                </Box>
            </Tabs.Root>

        </Box >
    );
};

export default SchoolTeacherView;
